import { createReduxModule } from "hooks-for-redux";
import { api } from "./api";
import { Assignment } from "./productExperiments";

export const homepageAbEnabled =
  process.env.NEXT_PUBLIC_HOMEPAGE_AB_ENABLED === "true";
type State = { ready: boolean; value: boolean; assignment: Assignment | null };
export const [
  useHomepageExperiment,
  { setHomepageExperiment },
  homepageExperimentStore,
] = createReduxModule(
  "homepageExperiment",
  { ready: !homepageAbEnabled, value: false, assignment: null } as State,
  { setHomepageExperiment: (_state, state: State) => state },
);
let generation = 0;
let exposure: Promise<void> | null = null;
let exposedKey: string | null = null;
const feature = "homepage_package_v1";

export async function prepareHomepageExperiment(enroll: boolean) {
  const current = ++generation;
  exposedKey = null;
  exposure = null;
  if (!homepageAbEnabled) {
    setHomepageExperiment({ ready: true, value: false, assignment: null });
    return;
  }
  setHomepageExperiment({ ready: false, value: false, assignment: null });
  let assignment: Assignment | null = null;
  try {
    const first = await api.post(
      "/experiments/assignment",
      { feature, enroll },
      { timeout: 5000 },
    );
    if (first.data.assignment) {
      // Confirm the HttpOnly cookie persisted. Cookie-blocked browsers stay outside
      // the experiment rather than being randomized again on each visit.
      const confirm = await api.post(
        "/experiments/assignment",
        { feature, enroll: false },
        { timeout: 5000 },
      );
      if (
        confirm.data.assignment?.key === first.data.assignment.key &&
        confirm.data.assignment?.arm === first.data.assignment.arm
      )
        assignment = confirm.data.assignment;
    }
  } catch {
    /* Fail open to the current production experience. */
  }
  if (current !== generation) return;
  setHomepageExperiment({
    ready: !assignment,
    value: assignment?.value === true,
    assignment,
  });
  // Record the same visible opportunity before either arm becomes interactive.
  // Hidden tabs wait for visibility; assignment alone is never an exposure.
  if (assignment && document.visibilityState === "visible")
    await recordHomepageExposure(assignment.token);
}
export const newGuestExperience = () =>
  homepageExperimentStore.getState().value;
export function resetHomepageExperiment() {
  ++generation;
  exposedKey = null;
  exposure = null;
  setHomepageExperiment({ ready: true, value: false, assignment: null });
}
export async function recordHomepageExposure(initialToken?: string) {
  const { assignment } = homepageExperimentStore.getState();
  if (!assignment || exposedKey === assignment.key) return;
  if (exposure) return exposure;
  const current = generation;
  exposure = (async () => {
    try {
      let token = initialToken;
      if (!token) {
        // A previously hidden tab may need a fresh signed token.
        const { data } = await api.post(
          "/experiments/assignment",
          { feature, enroll: false },
          { timeout: 5000 },
        );
        const fresh = data.assignment;
        if (
          !fresh ||
          fresh.key !== assignment.key ||
          fresh.arm !== assignment.arm
        ) {
          if (current === generation) resetHomepageExperiment();
          return;
        }
        token = fresh.token;
      }
      await api.post("/experiments/exposure", { token }, { timeout: 5000 });
      if (current === generation) exposedKey = assignment.key;
    } catch {
      /* A later visibility/navigation opportunity can retry. */
    } finally {
      if (current === generation) {
        exposure = null;
        setHomepageExperiment({
          ...homepageExperimentStore.getState(),
          ready: true,
        });
      }
    }
  })();
  return exposure;
}
export async function observeHomepageGame(type: string, gameId: unknown) {
  if (!homepageAbEnabled || !["game_started", "game_finished"].includes(type))
    return;
  try {
    await api.post(
      "/experiments/observation",
      { type, gameId },
      { timeout: 5000 },
    );
  } catch {
    /* Optional diagnostic; never block gameplay. */
  }
}

export async function observeHomepagePlacement(gameNr: number, gameId: string) {
  if (
    !homepageAbEnabled ||
    !Number.isInteger(gameNr) ||
    gameNr < 1 ||
    gameNr > 5
  ) return;
  try {
    await api.post(
      "/experiments/observation",
      { type: `placement_game_${gameNr}`, gameId },
      { timeout: 5000 },
    );
  } catch { /* Optional diagnostic; never block gameplay. */ }
}

// Serialize a quick Practice switch behind its limit event. Server uniqueness
// deduplicates remounts/reloads; failures never interrupt a game or mode change.
let leagueLimitRequest: Promise<void> = Promise.resolve();
export function observeHomepageLeagueLimit(type: "league_limit_reached" | "league_limit_practice") {
  const { assignment, ready } = homepageExperimentStore.getState();
  if (!homepageAbEnabled || !assignment || !ready) return Promise.resolve();
  const current = generation;
  leagueLimitRequest = leagueLimitRequest.then(async () => {
    if (current !== generation) return;
    try {
      await api.post("/experiments/observation", { type }, { timeout: 5000 });
    } catch { /* Optional client diagnostic. */ }
  });
  return leagueLimitRequest;
}

// Lifecycle refresh does not enroll visitors or change an established name.
export async function refreshHomepageExperiment() {
  const before = homepageExperimentStore.getState().assignment;
  if (!homepageAbEnabled || !before) return;
  const current = generation;
  try {
    const { data } = await api.post(
      "/experiments/assignment",
      { feature, enroll: false },
      { timeout: 5000 },
    );
    if (current !== generation) return;
    if (!data.assignment) resetHomepageExperiment();
  } catch {
    /* Keep the displayed experience during transient network failures. */
  }
}
